/**
 * `ErrorCode` → 中文提示。
 *
 * 为什么不直接显示服务端的 `message`：那条文案是给开发者看的（会带上内部细节），
 * 而玩家需要的是「我现在该做什么」。两者的受众不同，不能共用一句话。
 * 没覆盖到的码一律退回服务端文案 —— 显示一句略生硬的话，好过显示空白。
 */
import type { ErrorCode } from '@poker/shared'

const TEXT: Partial<Record<ErrorCode, string>> = {
  // 行动
  E_NOT_ACTOR: '这一桌正在你打开的另一个窗口里操作',
  E_NOT_YOUR_TURN: '还没轮到你',
  E_STALE_ACTION: '牌局已经往前走了，这一步没生效',
  E_FUTURE_ACTION: '牌局还没走到这一步',
  E_HAND_MISMATCH: '这手牌已经结束',
  E_HAND_NOT_RUNNING: '牌局还没开始',
  E_ILLEGAL_ACTION: '这个动作现在做不了',
  E_BET_TOO_SMALL: '加注额低于最小加注',
  E_BET_TOO_LARGE: '加注额超过你的筹码',
  E_BET_NOT_INTEGER: '金额必须是整数',
  E_ACTION_NOT_REOPENED: '这轮不能再加注，只能跟注或弃牌',
  E_NO_TIME_BANK: '时间银行已用完',
  // 座位
  E_SEAT_TAKEN: '这个座位刚被人坐了，换一个',
  E_SEAT_INVALID: '这个座位不存在',
  E_SEAT_RESERVED: '这个座位已被预留，换一个',
  E_ALREADY_SEATED: '你已经在座位上了',
  E_NOT_SEATED: '你还没坐下，先选个座位',
  E_SEAT_NOT_YOURS: '这不是你的座位',
  E_CANNOT_STAND_MIDHAND: '本手结束后你会离座',
  E_SEAT_CHANGE_MIDHAND: '这手打完再换座',
  E_REBUY_NOT_ALLOWED: '这个房间不能补码',
  // ★ 买分与转账的四个码**刻意不进这张表**，见文件末尾的 `topUpErrorText` /
  //   `transferErrorText`：服务端那句话带着具体额度（「筹码已达起始线 2000」），
  //   通用句「现在不能买分」会把唯一有用的那个数字盖掉。
  // 房间 / 权限
  E_ROOM_FULL: '房间已满',
  E_ROOM_CLOSED: '房间已关闭',
  E_ROOM_NOT_FOUND: '房间不存在，检查一下房间码',
  E_ROOM_CODE_INVALID: '房间码不对，再核对一下',
  E_NOT_HOST: '只有房主能这么做',
  E_CANNOT_KICK_SELF: '不能踢自己',
  E_CONFIG_LOCKED: '牌局进行中改不了这项设置',
  E_BOT_LIMIT: 'AI 数量已达上限',
  E_SEATS_OCCUPIED: '没有空位了',
  E_NOT_ENOUGH_PLAYERS: '人数不够，至少要 2 人',
  E_GAME_ALREADY_RUNNING: '牌局已经开始了',
  // 连接
  E_RATE_LIMITED: '点得太快了，等一下再试',
  E_NOT_AUTHENTICATED: '登录已失效，重新进一次房间',
  E_AUTH_EXPIRED: '登录已过期，重新进一次房间',
  E_SESSION_SUPERSEDED: '这一桌已在别处打开',
  E_INTERNAL: '服务器出错了，再试一次',
}

export function errorText(code: ErrorCode, fallback = ''): string {
  return TEXT[code] ?? (fallback === '' ? '这一步没成功，再试一次' : fallback)
}

/**
 * 被拒绝的动作要告诉玩家的一句话。
 * `appliedBy` 比错误码本身更能解释「为什么我点了却没生效」。
 */
export function rejectionText(code: ErrorCode, appliedBy: 'you' | 'otherDevice' | 'timeout' | null): string {
  switch (appliedBy) {
    case 'otherDevice':
      return '这一步已在别处完成'
    case 'timeout':
      return '超时了，已自动过牌或弃牌'
    case 'you':
      return '这一步已经出过了'
    default:
      return errorText(code)
  }
}

/**
 * 买分被拒时说什么。
 *
 * 这两个码是整套错误里**唯一**服务端文案比本地文案更有用的：
 * `E_TOPUP_NOT_ALLOWED` 的原因有四种（已达起始线 / 已有一笔待生效 / 牌局已结算 /
 * 没入座），而服务端那句话把具体的那一个连同额度一起说了。本地再写一句
 * 「现在不能买分」等于把唯一有信息量的部分丢掉。
 *
 * 服务端没给话时才兜底 —— 空白提示比生硬提示更糟。
 */
export function topUpErrorText(code: ErrorCode, message: string): string {
  if (message !== '') return message
  if (code === 'E_TOPUP_NOT_ALLOWED') return '现在不能买分'
  if (code === 'E_TOPUP_NOT_FOUND') return '这笔申请已经处理过了'
  return errorText(code, message)
}

/**
 * 转账被拒时说什么。理由与买分完全一样：服务端知道是三个上限里的**哪一个**顶住了
 * （起始线 / 对方筹码 / 申请额），本地再写一句「现在不能转账」等于把唯一有信息量的
 * 部分丢掉。空文案才兜底 —— 空白提示比生硬提示更糟。
 */
export function transferErrorText(code: ErrorCode, message: string): string {
  if (message !== '') return message
  if (code === 'E_TRANSFER_NOT_ALLOWED') return '现在不能转账'
  if (code === 'E_TRANSFER_NOT_FOUND') return '这笔申请已经处理过了'
  return errorText(code, message)
}
